import React, { Fragment, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Button, CircularProgress, Dialog, IconButton, TextField, Tooltip } from '@material-ui/core';
import SlidePanel from 'Components/SlidePanel/SlidePanel';
import JobTableList from 'Components/modals/SelectJobModal/JobTableList';
import TemplateTableList from './TemplateTableList';
import DmsService from 'Services/DMS/dms-api.service';
import { useAuth } from 'Contexts/AuthProvider';
import AlertToastr from 'utils/alert';
import { connect } from 'react-redux';
import useTranslation from 'Hooks/useTranslation';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { filterArray } from '../../../../utils/functions';
import { useBoxes } from 'Contexts/BoxContext';
import useJobSubscription from 'Components/SignalrSubscribes/JobSubscription.component';
import debounce from 'lodash.debounce';

// Jobs out of subscription (outside data retention or with all units disabled) can't load templates
const isJobSelectable = (job) =>
    job && !job.isDeleting && job.isInSubscription !== false;

const LoadTemplateModal2 = props => {
    const {
        isOpen,
        isOpenChangeHandler,
        jobs,
        loadedTemplate,
        onTemplateLoaded,
        setCurrentJobUnits,
        currentJob,
        licensesNames
    } = props;

    const [isTemplateSelectionStep, setIsTemplateSelectionStep] = useState(false);
    const { changeJobsGlobalFilters } = useBoxes();
    const { win } = useAuth();
    const translation = useTranslation();
    const [searchJob, setSearchJob] = useState('');
    const [errors, setErrors] = useState({
        type: 'error',
        messages: null,
    });
    const [selectedJob, setSelectedJob] = useState('');
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState((loadedTemplate && !loadedTemplate.modified && loadedTemplate.id) || '');
    const [searchTemplate, setSearchTemplate] = useState('');
    const [templateLoadedResponse, setTemplateLoadedResponse] = useState({ tabs: null, template: null });
    const [templateLoading, setTemplateLoading] = useState(false);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [isNextBtnClicked, setIsNextBtnClicked] = useState(true);
    const { subscribeToJob } = useJobSubscription();
    const [pagedJobs, setPagedJobs] = useState([]);
    const [lastJobsCount, setLastJobsCount] = useState(0);
    const [isLoadingJobs, setIsLoadingJobs] = useState(false);
    const isLoadingJobsRef = useRef(false);
    const listRef = useRef(null);

    const allowedJobs = useMemo(() => {
        if (!jobs) return [];
        return jobs.filter((job) => !job.isDeleting);
    }, [jobs]);

    const displayedJobs = useMemo(() => {
        if (!pagedJobs.length || !allowedJobs.length) return [];
        return pagedJobs.filter((job) =>
            allowedJobs.some((allowedJob) => allowedJob.id === job.id)
        );
    }, [pagedJobs, allowedJobs]);

    const pagedJobById = useMemo(() => {
        const m = Object.create(null);
        pagedJobs.forEach((j) => {
            m[j.id] = j;
        });
        return m;
    }, [pagedJobs]);

    const filteredJobs = useMemo(() => {
        const list = searchJob && searchJob.trim() !== ""
            ? filterArray(allowedJobs, searchJob)
            : displayedJobs;
        return list.map((job) => {
            const p = pagedJobById[job.id];
            return p && typeof p.isInSubscription === "boolean"
                ? { ...job, isInSubscription: p.isInSubscription }
                : job;
        });
    }, [allowedJobs, displayedJobs, searchJob, pagedJobById]);

    const filteredTemplates = useMemo(() => {
        if (!setSearchTemplate)
            return templates;
        return templates?.filter(item => {
            const name = item.name.toLowerCase();
            const search = searchTemplate.toLowerCase();
            return name.includes(search);
        });
    }, [templates, searchTemplate]);

    const fetchTemplates = useCallback(async (jobId) => {
        setIsLoadingTemplates(true);
        try {
            const response = await DmsService.getTemplatesByJobId(jobId);
            setTemplates(response.data || []);
        } catch (error) {
            if (error && error.response && error.response.data && error.response.data.Message)
                AlertToastr.showErrorAlert(error.response.data.Message);
        } finally {
            setIsLoadingTemplates(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            setPagedJobs([]); 
            setLastJobsCount(0);
        }
    }, [isOpen, searchJob]);

    useEffect(() => {
        if (!isOpen) {
            isLoadingJobsRef.current = false;
            setIsLoadingJobs(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            isLoadingJobsRef.current = true;
            setIsLoadingJobs(true);
            const debouncedFetchJobs = debounce(() => {
                DmsService.getJobsShortPaged(0, 40, [], searchJob)
                    .then((data) => {
                        setPagedJobs(data);
                        isLoadingJobsRef.current = false;
                        setIsLoadingJobs(false);
                    })
                    .catch((err) => {
                        console.error(err);
                        isLoadingJobsRef.current = false;
                        setIsLoadingJobs(false);
                    });
            }, 300);

            debouncedFetchJobs();
            return () => {
                debouncedFetchJobs.cancel();
                isLoadingJobsRef.current = false;
                setIsLoadingJobs(false);
            };
        }
    }, [isOpen, searchJob]);

    useEffect(() => {
        if (isOpen && !isNextBtnClicked && selectedJob)
            fetchTemplates(selectedJob);
    }, [fetchTemplates, isNextBtnClicked, isOpen, selectedJob]);

    const loadMoreJobs = useCallback(() => {
        if (isLoadingJobsRef.current) return;
        isLoadingJobsRef.current = true;
        setIsLoadingJobs(true);
        const skip = pagedJobs.length;
        DmsService.getJobsShortPaged(skip, 40, [], searchJob)
            .then((data) => {
                if (data && data.length > 0) {
                    setPagedJobs((prevJobs) => [...prevJobs, ...data]);
                }
            })
            .catch((err) => console.error(err))
            .finally(() => {
                isLoadingJobsRef.current = false;
                setIsLoadingJobs(false);
            });
    }, [pagedJobs.length, searchJob]);

    useEffect(() => {
        const container = listRef.current;
        if (!container) return;

        const handleScroll = debounce(() => {
            const threshold = 100;
            if (
                container.scrollTop + container.clientHeight >=
                container.scrollHeight - threshold
            ) {
                loadMoreJobs();
            }
        }, 200);

        container.addEventListener("scroll", handleScroll);
        return () => {
            container.removeEventListener("scroll", handleScroll);
            handleScroll.cancel();
        };
    }, [loadMoreJobs]);

    useEffect(() => {
        if (loadedTemplate && loadedTemplate.id && !loadedTemplate.modified && isOpen) {
            setSelectedTemplate(loadedTemplate.id);
        }
    }, [loadedTemplate, isOpen]);

    useEffect(() => {
        if (!allowedJobs || allowedJobs.length <= lastJobsCount) return;
        if (currentJob) {
            const currentJobInList = allowedJobs.find(item => item.id === currentJob.id);
            if (currentJobInList) {
                setSelectedJob(currentJobInList.id);
                setIsTemplateSelectionStep(true);
                setIsNextBtnClicked(false);
            }
        } else {
            setSelectedJob(allowedJobs[0].id);
        }
        setLastJobsCount(allowedJobs.length);
    }, [allowedJobs, lastJobsCount, currentJob]);

    // The current job is preselected before paged jobs arrive; step back if it turns out to be out of subscription
    useEffect(() => {
        if (isTemplateSelectionStep && selectedJob && pagedJobById[selectedJob]?.isInSubscription === false) {
            setIsTemplateSelectionStep(false);
            setTemplates([]);
        }
    }, [isTemplateSelectionStep, selectedJob, pagedJobById]);

    useEffect(() => {
        if (filteredJobs.length) {
            const selected = filteredJobs.find((job) => job.id === selectedJob);
            if (!isJobSelectable(selected)) {
                const firstSelectableJob = filteredJobs.find(isJobSelectable);
                setSelectedJob(firstSelectableJob ? firstSelectableJob.id : '');
            }
        } else {
            setSelectedJob("");
        }
    }, [filteredJobs, searchJob, selectedJob, isOpen]);

    const handleSuccessTemplateLoad = (tabs, template) => {
        onTemplateLoaded(selectedTemplate, selectedJob, tabs, template);
        isOpenChangeHandler(false);
    };

    useEffect(() => {
        const fetchJobById = async (jobId) => {
            if (jobId && allowedJobs && allowedJobs.length) {
                let response = await DmsService.getJobsById(jobId);
                if (response) {
                    const job = allowedJobs.find((x) => x.id === jobId);
                    if (job) {
                        job.units = response?.units;
                        setCurrentJobUnits(response?.units);
                    }
                }
            }
        };
        if (selectedJob && isOpen) {
            fetchJobById(selectedJob);
        }
    }, [selectedJob, setCurrentJobUnits, allowedJobs, isOpen]);

    const handleLoadTemplateWithSubscription = async () => {
        if (!selectedJob || !selectedTemplate || templateLoading) {
            console.error("Missing required data for subscription.", {
                selectedJob,
                selectedTemplate,
                templateLoading
            });
            return;
        }

        const selectedJobData = allowedJobs.find(job => job.id === selectedJob);
        if (!selectedJobData || !selectedJobData.licenseId) {
            console.error("Selected job or license ID is missing.", {
                selectedJob,
                licenseId: selectedJobData?.licenseId
            });
            return;
        }

        await subscribeToJob(
            selectedJobData.id,
            selectedJobData.licenseId,
            handleLoadTemplate
        );
    };

    const handleLoadTemplate = () => {
        if (selectedJob && selectedTemplate) {
            setTemplateLoading(true);

            DmsService.loadTemplate(selectedTemplate, selectedJob, win.id)
                .then(response => {
                    const tabs = response && response.data && response.data.tabs;
                    const messages = response && response.data && response.data.messages;
                    const template = templates.find(template => template.id === selectedTemplate);
                    const dateType = response?.data?.tabs[0]?.items[0]?.filters?.dateType;
                    if (dateType) {
                        changeJobsGlobalFilters({ dateType });
                    }
                    if (messages && messages.length) {
                        setErrors({ type: 'warning', messages });
                        setTemplateLoadedResponse({
                            tabs,
                            template,
                        });
                    } else {
                        handleSuccessTemplateLoad(tabs, template);
                    }

                    setTemplateLoading(false);
                })
                .catch(error => {
                    if (error.response && error.response.data && error.response.data.message) {
                        setErrors({
                            type: 'error',
                            messages: [error.response.data.message],
                        });
                    }
                    setTemplateLoading(false);
                });
        }
    };

    const handleErrors = () => {
        setErrors({ type: 'error', messages: null });
        if (errors.type === 'warning') {
            const { tabs, template } = templateLoadedResponse;
            if (tabs && template) {
                handleSuccessTemplateLoad(tabs, template);
            }
        }
    };

    const handleCancel = () => {
        isOpenChangeHandler(false);
        setSelectedTemplate('');
    };

    const renderErrorDialog = () => {
        const title = errors.type === 'error' ? 'Error' : 'Warning';
        const iconClass = errors.type === 'error'
            ? 'modalTitle hasIcon error'
            : 'modalTitle hasIcon warning';
        return (
            <Dialog open={Boolean(errors.messages)}>
                <div className="modalTitleHolder mb16">
                    <h2 className={iconClass}>{title}</h2>
                </div>
                <ul className="errorList">
                    {errors.messages && errors.messages.map((errorMessage, index) => (
                        <li key={index}>{errorMessage}</li>
                    ))}
                </ul>
                <div className="btnHolder end mt22 fixedWidthBtn">
                    <Button color={'primary'}
                            onClick={handleErrors}
                            variant={'contained'}>
                        Ok
                    </Button>
                </div>
            </Dialog>
        );
    };

    const backButton = isTemplateSelectionStep ? (
        <div className="backLinkHolder">
            <Tooltip disableFocusListener disableTouchListener title={translation.selectJobLabel}>
                <IconButton
                    aria-label="back"
                    size="small"
                    onClick={() => {
                        setIsTemplateSelectionStep(false);
                        setTemplates([]);
                    }}
                >
                    <ArrowBackIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>
        </div>
    ) : null;

    const footer = !isTemplateSelectionStep ? (
        <Fragment>
            <Button onClick={handleCancel} variant="outlined">{translation.cancelButtonLabel}</Button>
            <Button
                onClick={() => {
                    setIsTemplateSelectionStep(true);
                    setIsNextBtnClicked(true);
                    fetchTemplates(selectedJob);
                }}
                variant="contained"
                color="primary"
                disabled={!selectedJob}
                id="loadTemplateModal2Btn-next"
            >
                {translation.nextButtonLabel}
            </Button>
        </Fragment>
    ) : (
        <Fragment>
            {templateLoading && <CircularProgress size={24} />}
            <Button onClick={handleCancel} variant="outlined">{translation.cancelButtonLabel}</Button>
            <Button
                onClick={handleLoadTemplateWithSubscription}
                variant="contained"
                color="primary"
                disabled={!(selectedTemplate && selectedJob) || templateLoading}
                id="loadTemplateModal2Btn-load"
            >
                {translation.loadButtonLabel}
            </Button>
        </Fragment>
    );

    return (
        <Fragment>
            <SlidePanel
                open={isOpen}
                onClose={handleCancel}
                title={translation.dashboardLoadTemplateLabel}
                headerStart={backButton}
                className="appSlidePanel--wide"
                footer={footer}
            >
                {!isTemplateSelectionStep ? (
                    <Fragment>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            name="search"
                            label={translation.dashboardSearchJobLabel}
                            type="text"
                            value={searchJob}
                            onChange={e => setSearchJob(e.target.value)}
                        />
                        <JobTableList
                            jobs={filteredJobs}
                            value={selectedJob ? String(selectedJob) : ''}
                            onChange={(event) => setSelectedJob(+event.target.value)}
                            isRowDisabled={(job) => !isJobSelectable(job)}
                            isRowMuted={(job) => job.isInSubscription === false}
                            licensesNames={licensesNames}
                            listRef={listRef}
                            isLoading={isLoadingJobs}
                        />
                    </Fragment>
                ) : (
                    <Fragment>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            name="search"
                            label={translation.dashboardSearchTemplateLabel}
                            type="text"
                            value={searchTemplate}
                            onChange={e => setSearchTemplate(e.target.value)}
                        />
                        <TemplateTableList
                            templates={filteredTemplates}
                            value={selectedTemplate}
                            onChange={setSelectedTemplate}
                            isLoading={isLoadingTemplates}
                        />
                    </Fragment>
                )}
            </SlidePanel>
            {renderErrorDialog()}
        </Fragment>
    );
};

const mapStateToProps = ({ dashboard }) => ({
    currentCompany: dashboard.currentCompany,
});

export default connect(mapStateToProps)(LoadTemplateModal2);